
import Vue from 'vue'
import { VueEditor } from 'vue2-editor'
import { apiService } from '@/service/api.service'
import AdminTableSubheader from '@/components/shared/AdminTableSubheader.vue'

const BLOCK_TYPES = [
  { type: 'header', label: 'Header', icon: 'mdi-format-header-1' },
  { type: 'hero', label: 'Hero', icon: 'mdi-format-title' },
  { type: 'text', label: 'Text', icon: 'mdi-format-paragraph' },
  { type: 'image', label: 'Image', icon: 'mdi-image-outline' },
  { type: 'button', label: 'Button', icon: 'mdi-gesture-tap-button' },
  { type: 'divider', label: 'Divider', icon: 'mdi-minus' },
  { type: 'spacer', label: 'Spacer', icon: 'mdi-arrow-expand-vertical' },
  { type: 'footer', label: 'Footer', icon: 'mdi-format-page-break' }
]

const DEFAULT_BLOCKS: Record<string, Record<string, unknown>> = {
  header: { type: 'header', bgColor: '#0f172a', textColor: '#ffffff', logoText: 'CORDATUS', title: '', subtitle: '' },
  hero: { type: 'hero', title: 'Your Title Here', subtitle: 'Subtitle goes here', textAlign: 'center' },
  text: { type: 'text', html: '<p>Hello <strong>{{name}}</strong>,</p><p>Your message here.</p>' },
  image: { type: 'image', url: '', alt: '', linkUrl: '' },
  button: { type: 'button', text: 'Get Started', url: '{{appUrl}}', bgColor: '#6366f1', textColor: '#ffffff' },
  divider: { type: 'divider', color: '#e2e8f0' },
  spacer: { type: 'spacer', height: 24 },
  footer: { type: 'footer', supportMail: '{{supportMail}}', companyName: 'Cordatus' }
}

const SKELETON_BLOCKS: Record<string, unknown>[] = [
  { type: 'header', bgColor: '#0f172a', textColor: '#ffffff', logoText: 'CORDATUS', title: 'Your Email Title', subtitle: 'A short subtitle for context' },
  { type: 'spacer', height: 8 },
  { type: 'hero', title: 'Welcome, {{name}}!', subtitle: 'We\'re glad to have you here. Here\'s what\'s new for you.', textAlign: 'center' },
  { type: 'divider', color: '#e2e8f0' },
  { type: 'text', html: '<p style="margin:0 0 14px">Hello <strong>{{name}}</strong>,</p><p style="margin:0 0 14px;color:#475569">Your message goes here. Keep it concise and focused — one clear goal per email performs best.</p><p style="margin:0;color:#475569">If you have any questions, don\'t hesitate to reach out to our support team.</p>' },
  { type: 'spacer', height: 8 },
  { type: 'button', text: 'Get Started', url: '{{appUrl}}', bgColor: '#6366f1', textColor: '#ffffff' },
  { type: 'spacer', height: 8 },
  { type: 'footer', supportMail: '{{supportMail}}', companyName: 'Cordatus' }
]

export default Vue.extend({
  name: 'TemplateBuilder',
  components: { VueEditor, AdminTableSubheader },

  data: () => ({
    saving: false,
    previewing: false,
    previewOpen: false,
    imageUploading: false,
    testEmail: '',
    selectedIdx: null as number | null,
    blockTypes: BLOCK_TYPES,
    form: {
      name: '',
      subject: '',
      previewText: '',
      description: '',
      blocks: [] as Record<string, unknown>[]
    },
    editorToolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }],
      ['link'],
      ['clean']
    ],
    snack: { show: false, text: '', color: 'success' }
  }),

  computed: {
    isEdit (): boolean { return !!this.$route.params.id },
    selectedBlock (): Record<string, unknown> | null {
      return this.selectedIdx !== null ? this.form.blocks[this.selectedIdx] : null
    },
    previewHtml (): string {
      const blocksHtml = this.form.blocks.map((b: Record<string, unknown>) => this.renderBlockHtml(b)).join('\n')
      return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:#eef2f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;padding:32px 0;-webkit-font-smoothing:antialiased}
  .wrap{max-width:600px;margin:0 auto;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 32px rgba(15,23,42,.10),0 1px 4px rgba(15,23,42,.06)}
</style>
</head>
<body><div class="wrap">${blocksHtml}</div></body>
</html>`
    }
  },

  async created () {
    if (this.isEdit) {
      await this.loadTemplate()
    } else {
      this.form.blocks = SKELETON_BLOCKS.map(b => ({ ...b }))
    }
  },

  methods: {
    async loadTemplate () {
      try {
        const res = await apiService.get(`admin-email-templates/${this.$route.params.id}`) as { data: { data: Record<string, unknown> } }
        const t = res.data.data
        this.form.name = (t.name as string) || ''
        this.form.subject = (t.subject as string) || ''
        this.form.previewText = (t.previewText as string) || ''
        this.form.description = (t.description as string) || ''
        this.form.blocks = (t.blocks as Record<string, unknown>[]) || []
      } catch {
        this.notify('Failed to load template', 'error')
      }
    },

    addBlock (type: string) {
      this.form.blocks.push({ ...DEFAULT_BLOCKS[type] })
      this.selectedIdx = this.form.blocks.length - 1
    },

    removeBlock (idx: number) {
      this.form.blocks.splice(idx, 1)
      if (this.selectedIdx === idx) this.selectedIdx = null
      else if (this.selectedIdx !== null && this.selectedIdx > idx) this.selectedIdx--
    },

    moveBlock (idx: number, dir: number) {
      const arr = this.form.blocks
      const target = idx + dir
      if (target < 0 || target >= arr.length) return
      ;[arr[idx], arr[target]] = [arr[target], arr[idx]]
      this.form.blocks = [...arr]
      this.selectedIdx = target
    },

    togglePreview () {
      this.previewOpen = !this.previewOpen
    },

    async save () {
      if (!this.form.name || !this.form.subject) {
        this.notify('Name and Subject are required', 'error')
        return
      }
      this.saving = true
      try {
        const payload = { ...this.form }
        if (this.isEdit) {
          await apiService.put(`admin-email-templates/${this.$route.params.id}`, payload)
        } else {
          const res = await apiService.post('admin-email-templates', payload) as { data: { data: { id: string } } }
          this.$router.replace(`/email-campaigns/templates/${res.data.data.id}`)
        }
        this.notify('Template saved')
      } catch {
        this.notify('Save failed', 'error')
      } finally {
        this.saving = false
      }
    },

    async sendPreview (email?: string) {
      if (!this.isEdit) { this.notify('Save the template first', 'warning'); return }
      this.previewing = true
      try {
        const payload = email ? { email } : {}
        await apiService.post(`admin-email-templates/${this.$route.params.id}/preview`, payload)
        this.notify(email ? `Preview sent to ${email}` : 'Preview email sent to your address')
      } catch {
        this.notify('Preview failed', 'error')
      } finally {
        this.previewing = false
      }
    },

    renderBlockHtml (block: Record<string, unknown>): string {
      const s = (v: unknown, fallback = '') => (v ? String(v) : fallback)
      const esc = (v: unknown) => String(v || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

      switch (block.type) {
        case 'header':
          return `<div style="background:${s(block.bgColor, '#0f172a')};padding:0">
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse">
              <tr>
                <td style="padding:28px 40px;text-align:center">
                  ${block.logoText ? `<div style="display:inline-block;font-size:10px;font-weight:800;letter-spacing:3px;text-transform:uppercase;color:${s(block.textColor, '#fff')};opacity:.55;margin-bottom:18px;padding:5px 12px;border:1px solid rgba(255,255,255,.15);border-radius:4px">${esc(block.logoText)}</div><br>` : ''}
                  ${block.title ? `<h1 style="margin:0 0 10px;font-size:28px;font-weight:700;line-height:1.2;color:${s(block.textColor, '#fff')};letter-spacing:-.3px">${esc(block.title)}</h1>` : ''}
                  ${block.subtitle ? `<p style="margin:0;font-size:15px;line-height:1.6;color:${s(block.textColor, '#fff')};opacity:.7">${esc(block.subtitle)}</p>` : ''}
                </td>
              </tr>
            </table>
          </div>`

        case 'hero':
          return `<div style="padding:44px 48px;text-align:${s(block.textAlign, 'center')};background:#ffffff">
            ${block.title ? `<h2 style="margin:0 0 12px;font-size:24px;font-weight:700;color:#0f172a;line-height:1.3;letter-spacing:-.2px">${esc(block.title)}</h2>` : ''}
            ${block.subtitle ? `<p style="margin:0;font-size:16px;color:#64748b;line-height:1.65">${esc(block.subtitle)}</p>` : ''}
          </div>`

        case 'text':
          return `<div style="padding:4px 48px 20px;font-size:15px;line-height:1.75;color:#334155;background:#ffffff">${s(block.html)}</div>`

        case 'image': {
          if (!block.url) {
            return '<div style="margin:16px 48px;padding:32px 20px;text-align:center;color:#94a3b8;font-size:13px;border:1.5px dashed #e2e8f0;border-radius:10px;background:#f8fafc">[ Image placeholder ]</div>'
          }
          const img = `<img src="${esc(block.url)}" alt="${esc(block.alt)}" style="max-width:100%;height:auto;border-radius:10px;display:block;margin:0 auto;box-shadow:0 2px 16px rgba(0,0,0,.08)">`
          return `<div style="padding:16px 48px;text-align:center;background:#ffffff">${block.linkUrl ? `<a href="${esc(block.linkUrl)}" style="display:inline-block">${img}</a>` : img}</div>`
        }

        case 'button':
          return `<div style="text-align:center;padding:24px 48px;background:#ffffff">
            <a href="${esc(block.url)}" style="display:inline-block;background:${s(block.bgColor, '#6366f1')};color:${s(block.textColor, '#fff')};padding:15px 40px;border-radius:8px;font-weight:600;font-size:15px;text-decoration:none;letter-spacing:.01em;box-shadow:0 2px 8px rgba(99,102,241,.35)">${esc(block.text)}</a>
          </div>`

        case 'divider':
          return `<div style="padding:4px 48px;background:#ffffff"><hr style="border:none;border-top:1px solid ${s(block.color, '#e2e8f0')}"></div>`

        case 'spacer':
          return `<div style="height:${s(block.height, '24')}px;background:#ffffff"></div>`

        case 'footer':
          return `<div style="background:#f8fafc;padding:28px 48px;text-align:center;border-top:1px solid #e2e8f0">
            ${block.companyName ? `<p style="margin:0 0 8px;font-size:13px;font-weight:700;color:#475569;letter-spacing:.3px;text-transform:uppercase">${esc(block.companyName)}</p>` : ''}
            ${block.supportMail ? `<p style="margin:0 0 12px;font-size:13px;color:#94a3b8">Questions? <a href="mailto:${esc(block.supportMail)}" style="color:#6366f1;text-decoration:none;font-weight:500">${esc(block.supportMail)}</a></p>` : ''}
            <p style="margin:0;font-size:11px;color:#cbd5e1;line-height:1.6">You are receiving this email because you have an account at Cordatus.<br>© ${new Date().getFullYear()} Cordatus. All rights reserved.</p>
          </div>`

        default:
          return ''
      }
    },

    iconFor (type: string): string {
      return BLOCK_TYPES.find(b => b.type === type)?.icon ?? 'mdi-square-outline'
    },
    labelFor (type: string): string {
      return BLOCK_TYPES.find(b => b.type === type)?.label ?? type
    },
    blockSummary (block: Record<string, unknown>): string {
      const html = typeof block.html === 'string' ? block.html.replace(/<[^>]+>/g, '').slice(0, 28) : ''
      return (block.title || block.text || block.logoText || html || '') as string
    },

    triggerImagePicker () {
      (this.$refs.imagePicker as HTMLInputElement).click()
    },

    onImageDrop (e: DragEvent) {
      const file = e.dataTransfer?.files?.[0]
      if (file) this.uploadImageFile(file)
    },

    onImageFilePick (e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) this.uploadImageFile(file)
      // reset so same file can be picked again
      ;(e.target as HTMLInputElement).value = ''
    },

    async uploadImageFile (file: File) {
      this.imageUploading = true
      try {
        const form = new FormData()
        form.append('image', file)
        const res = await apiService.post('admin-email-image-upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }) as { data: { url: string } }
        if (this.selectedBlock) {
          this.$set(this.selectedBlock, 'url', res.data.url)
        }
        this.notify('Image uploaded')
      } catch {
        this.notify('Image upload failed', 'error')
      } finally {
        this.imageUploading = false
      }
    },

    notify (text: string, color = 'success') {
      this.snack = { show: true, text, color }
    }
  }
})
